export const commonTranslations = {
  en: {
    appTitle: "Iceland Camping",
    sevenDayWeather: "7-Day Weather Nearby",
    heroStayMoveTitle: "Find calmer camping before the weather turns.",
    heroStayMoveSubtitle: "Chase the Weather compares nearby campsites and shows where conditions are likely calmer, drier or warmer over the next few days.",
    heroCta: "Find a better spot",
    // Ticket 411 (#411) — homepage hero seasonal variants, see src/config/homepageHero.js
    heroWinterAuroraTitle: "Find the best weather — and the best chances of seeing the Northern Lights",
    heroWinterAuroraSubtitle: "Compare weather across Iceland, find a better destination, and see where conditions are best for watching the Northern Lights.",
    heroAprilTitle: "Find the best weather",
    heroAprilSubtitle: "Compare weather across Iceland and find a better destination.",
    heroWinterCta: "Explore the weather",
    myLocation: "My location",
    dark: "Dark",
    light: "Light",
    top5Title: "Best campsites based on your location",
    upToDate: "Up to date.",
    campsite: "Campsite",
    distance: "Distance",
    score: "Score",
    day: "Day",
    weather: "Weather",
    sortedBy: "Sorted by weekly score, then nearest to you.",
    offlineRdMessage: "Chase the Weather is now ready to use offline.",

    // Geolocation

    locating: "Locating…",

    // Search & selection
    searchCampsites: "Search campsites…",
    selectCampsite: "Select campsite",
    selectOnMap: "Select on map",

    // Weather
    tempMin: "Temp min",
    tempMax: "Temp max",
    windMax: "Max wind",
    rain: "Rain",

    tempMinShort: "Min",
    tempMaxShort: "Max",
    windMaxShort: "Wind",
    rainShort: "Rain",

    // Ratings (short)
    best: "Best",
    good: "Good",
    ok: "Ok",
    fair: "Fair",

    // Install
    installAsApp: "Install as an app",
    installOnDevice: "Install on this device",

    // Quality scale
    excellent: "Excellent",
    goodQuality: "Good",
    fairQuality: "Fair",
    poor: "Poor",
    bad: "Bad",

    // Weather descriptions
    clearSky: "Clear sky",
    mainlyClear: "Mainly clear",
    partlyCloudy: "Partly cloudy",
    overcast: "Overcast",
    unknownWeather: "Unknown weather",

    fog: "Fog",
    rimeFog: "Rime fog",

    lightDrizzle: "Light drizzle",
    drizzle: "Drizzle",
    heavyDrizzle: "Heavy drizzle",

    lightRain: "Light rain",
    heavyRain: "Heavy rain",

    freezingRain: "Freezing rain",
    heavyFreezingRain: "Heavy freezing rain",

    lightSnow: "Light snow",
    snow: "Snow",
    heavySnow: "Heavy snow",
    snowGrains: "Snow grains",

    showers: "Showers",
    heavyShowers: "Heavy showers",
    violentShowers: "Violent showers",

    thunderstorm: "Thunderstorm",
    thunderHail: "Thunder + hail",
    severeThunderHail: "Severe thunder + hail",

    // Ticket 402 (#402) — temporal daily-summary narratives. Presentation
    // only (src/lib/dailyWeatherSummary.js), describing a meaningful wet/dry
    // transition or a brief isolated episode within an otherwise dry usable
    // day, rather than letting one severe hour define the whole headline.
    dailySummaryRainEarlyDryLater: "Rain early, dry later",
    dailySummaryDryEarlyRainLater: "Dry early, rain later",
    dailySummaryBriefShowers: "Brief showers, otherwise dry",

    // Score explanation
    scoreExplanationTitle: "How camping conditions are scored",

    scoreExplanationIntro:
      "Chase the Weather gives each day a score from 0 to 10 based on weather conditions at the campsite.",

    scoreExplanation_Temperature: "Temperature",

    scoreExplanation_Wind: "Wind",

    scoreExplanation_Rain: "Rain",

    scoreExplanationFinalTitle: "Final score",

    scoreExplanationFinalText:
      "The score starts from temperature and is adjusted based on wind and precipitation. The final result is always kept between 0 and 10.",

    scoreExplanationOutro:
      "Higher scores generally mean calmer and more comfortable camping conditions.",

    // Misc UI
    loadingMoreCampsites: "Loading more campsites…",
    weeklyScore: "Weekly score:",
    from: "from",
    scored: "scored",
    away: "away",
    total: "Total",
    days: "days",
    pts: "pts",

    // UX Miði #376 — progressive disclosure entrypoints (forecast/ranking
    // supporting-detail, collapsed by default below HomeDecisionCard).
    forecastShowDetailsCta: "See detailed forecast",
    forecastHideDetailsCta: "Hide forecast",

    // Pro (Wind direction & shelter)
    proPreviewCta: "Pro preview",
    windDirectionShelterTitle: "Wind direction & shelter (Pro)",
    windDirectionShelterSubtitle:
      "Unlock wind direction and a shelter score to pick calmer campsites.",
    windLabel: "WIND",
    shelterLabel: "SHELTER",
    shelteredFrom: "sheltered from",
    shelterHigh: "High",
    shelterMedium: "Medium",
    shelterLow: "Low",
    seePro: "See Pro",
    shelterTooltipPrefix: "Shelter",

    // Toasts / retry
    retrying: "Retrying…",
    retry: "Retry",

    aboutTitle: "About Chase the Weather",

    aboutIntro:
      "Chase the Weather helps campers in Iceland make better decisions based on real weather conditions.",

    aboutGoal:
      "Instead of only showing forecasts, Chase the Weather evaluates camping conditions and highlights when nearby campsites may offer better weather.",

    aboutFeaturesTitle: "Chase the Weather includes:",

    aboutFeatures: [
      "7-day campsite forecasts",
      "Wind, rain and temperature analysis",
      "Weekly ranking of campsites based on conditions",
      "Route Planner suggestions when better weather may be nearby",
    ],

    aboutProTitle: "With Chase the Weather Pro, you also get:",

    aboutProFeatures: [
      "Wind direction information",
      "Shelter score to identify calmer locations",
      "Detailed campsite comparisons",
      "More insight into when moving campsites may be worthwhile",
    ],

    // Ticket 416 (#416) — compact Northern Lights section. auroraInfo*
    // (same-assessment, no-guarantee, seasonal note) are shared keys, see
    // translations.northernLights.js.
    aboutAuroraTitle: "Northern Lights",
    // Ticket 416 (#416) Round 2 correction: the prior wording ("at your
    // campsite") falsely implied a per-site assessment — NorthernLightsCard
    // always requests the fixed AURORA_CANDIDATE_LOCATION_IDS roster
    // (src/config/auroraCandidates.js), independent of the selected site.
    aboutAuroraBody:
      "We assess Northern Lights activity together with local weather conditions to evaluate tonight's viewing conditions at the places we check.",
    // Ticket #431: named locations, reasons and place comparison are open to
    // every tier now (full access for the "northern_lights_free_v1"
    // experiment) — no longer a Pro-exclusive addition.
    aboutAuroraProNote: "Named locations, reasons and place comparison, when results support them.",
    aboutAuroraLink: "See the Northern Lights forecast",

    aboutOutro:
      "Chase the Weather is designed specifically for Icelandic camping conditions and continues to evolve based on user feedback.",

    aboutContact: "Questions, feedback, or issues?",

    aboutEmailLabel: "Reach us at",

    aboutSupportTitle: "Support",
    aboutSupportBody:
      "Eltum Veðrið has received support from the Icelandic Technology Development Fund for continued development of the project.",
    aboutSupportLogoAlt: "Logo of the Icelandic Technology Development Fund",

    footerDataBy: "Data by Open-Meteo. Forecast includes temperature, rain, wind, & weather codes.",
    footerAbout: "About",
    blogNav: "Blog",
    footerContact: "Contact",
    footerSupport: "With support from the Icelandic Technology Development Fund",
    backToForecast: "Back to forecast",

    brandTagline: "Find better weather",

    nearest: "Nearest",

    // Login / Identity (FLAT keys)
    close: "Close",
    loginEmailPlaceholder: "you@example.com",
    noPasswordNeeded:
      "No password is needed to log in. Just enter your email and we'll send you a link to log in.",
    login: "Login",
    enterEmailToContinue: "Enter your email to continue",
    loading: "Loading...",
    continue: "Continue",
    loginRequired: "Login required",
    pleaseLoginToContinue: "Please log in to continue.",
    checkoutSuccess: "Pro unlocked!",
    checkoutCancelled: "Checkout cancelled.",

    // Pro CTA
    proCtaTitle: "Get Pro",
    proCtaSubtitle: "Log in to purchase and unlock Pro features.",
    proCtaTooltip: "Log in → checkout → unlock Pro",
    alreadyPro: "Already Pro",
    proActive: "Pro active ✓",
    loggedIn: "Logged in",
    proUpgrade: "Upgrade to Pro – unlock Route Planner",

    // Weekly ranking (Top5Leaderboard) — Pro CTA is context-specific here,
    // not the shared proUpgrade/proCtaSubtitle text (which references Route
    // Planner, unrelated to this card).
    weeklyRankingUpgradeCta: "See more top campsites with Pro",
    weeklyRankingUpgradeBody:
      "See more of the best campsites, plus wind direction and shelter insight to help choose a calmer spot.",

    // UX Miði #376 — progressive disclosure entrypoint for the ranking card itself
    // (distinct from weeklyRankingUpgradeCta, which is the Pro upsell inside it).
    weeklyRankingShowDetailsCta: "See more campsites",
    weeklyRankingHideDetailsCta: "Hide list",

    // Billing portal
    manageSubscription: "Manage",
    billingPortal: "Billing portal",
    openingBillingPortal: "Opening billing portal…",
    successStatusPending: "Pending activation",
    successDelayedActivation: "Your access will activate shortly. If this takes longer than expected, please refresh the page.",
    offlineReadyTitle: "Ready for offline",
    offlineReadyMsg: "Chase the Weather is cached and can work offline.",

    proUntil: "Pro until",
    proManageHint: "Manage your subscription and invoices.",

    availabilityClosedUntilMay: "Most campsites are closed during winter (often until May).",
    availabilityOpenSeason: "Open during the summer season.",
    availabilityOpenAllYear: "Open all year.",

    availabilityMostOpenInMay: "Most campsites open in May. Check the campsite for confirmation.",
    availabilityClosedBadge: "Closed (winter)",
    seasonClosedInfo:
      "Many campsites are currently closed, but weather insights can still help you plan ahead or travel off-season.",
    winterModeActive: "Winter mode",
    winterModeTooltip: "Wind and precipitation weigh more than temperature during winter.",
    windGust: "Wind gust",

    termsTitle: "Terms and Disclaimer",

    termsOperatorStatement: "CampCast is a brand used for Eltum Veðrið.",

    termsIntro1:
      "Eltum Veðrið provides weather forecasts and estimates of weather conditions along travel routes based on available meteorological data. This information is provided for informational purposes only and may be inaccurate, incomplete, or outdated.",

    termsIntro2:
      "Weather conditions in Iceland can change rapidly, and actual conditions may differ from forecasts or estimates shown in the service.",

    termsIntro3:
      "Eltum Veðrið does not provide driving instructions and does not guarantee travel safety. Users are solely responsible for their own travel decisions and should always check official weather and road information before travelling.",

    termsSourcesTitle: "Users are encouraged to consult official sources such as:",

    termsLiability:
      "Eltum Veðrið and its operators accept no liability for accidents, damages, delays, or losses resulting from the use of the service or reliance on information provided within it.",
    termsDisclaimerAgree: "I agree to the Terms of Service and Disclaimer",
    termsDisclaimerLink: "Terms and Disclaimer",

    subscribeTermsPrefix: "By continuing you agree to the",
    subscribeEmailHelp: "We use your email for receipts and subscription information.",

    locationEnableDistances: "Enable location to see distances to campsites",

    lockedPro: "Available in Pro",

    weatherNearbyHint: "Weather based on nearby forecast data",
    weatherNearbyTooltip:
      "Forecast is based on the nearest available weather data and may vary slightly from exact campsite conditions.",

    precipLightRain: "Light rain",
    precipModerateRain: "Moderate rain",
    precipHeavyRain: "Heavy rain",

    precipLightSnow: "Light snow",
    precipModerateSnow: "Moderate snow",
    precipHeavySnow: "Heavy snow",

    precipVeryHeavyRain: "Very heavy rain",
    precipVeryHeavySnow: "Very heavy snow",

    precipLateRain: "Rain late",
    precipLateSnow: "Snow late",
    precipLight: "Light precipitation",
    precipIntermittentRain: "Intermittent rain",
    precipMixed: "Mixed precipitation",
    precipShowers: "Showers",

    privacyTitle: "Privacy Policy",
    privacyIntro1:
      "Eltum Veðrið respects user privacy and aims to handle personal data responsibly and securely.",
    privacyIntro2:
      "We may collect basic user information such as email address, account details, and general usage data in order to provide the service, maintain subscriptions, and improve the user experience.",
    privacyPayments:
      "Payment information is processed by Paddle and is not stored on Eltum Veðrið servers.",
    privacyUsage:
      "We may also collect anonymous or aggregated usage data for analytics purposes to improve the functionality, stability, and user experience of the service.",
    privacySharing:
      "Eltum Veðrið does not sell personal data and does not share it with third parties except where necessary to process payments, operate the service, or comply with legal obligations.",
    privacyRetention:
      "Personal data is only retained for as long as necessary for operating the service or as required by law.",
    privacyRights:
      "Users may request information about data connected to their account or request deletion of their data by contacting the service provider.",
    privacyContactTitle: "Contact",
    privacyContactText:
      "If you have any questions about privacy or would like to request deletion of your data, you can contact us at:",
    privacyChanges:
      "Eltum Veðrið may update this privacy policy from time to time. The latest version will always be published on the website.",
    privacyClose: "Close",

    termsPrivacyAgreePrefix: "I agree to the",
    termsPrivacyAgreeAnd: "and",
    privacyLinkLabel: "Privacy Policy",
    footerTerms: "Terms",
    footerPrivacy: "Privacy",

    refundTitle: "Refund Policy",
    refundIntro:
      "Eltum Veðrið offers subscriptions (monthly and yearly) and one-time passes (a 30-day travel pass and an annual pass) for access to Pro features.",
    refundCancel:
      "Subscribers can cancel their subscription at any time through the billing portal. Access remains active until the end of the paid period.",
    refundPasses:
      "Passes are one-time purchases with no automatic renewal and expire automatically at the end of their validity period. No cancellation is needed.",
    refundWindow:
      "Eltum Veðrið offers a 14-day refund period from the first payment of a new subscription and from the purchase of a pass.",
    refundAfterWindow: "After the 14-day refund period ends, payments are not refunded.",
    refundRenewals: "Refunds do not apply to subscription renewals unless required by law.",
    refundAccessRevoked:
      "When a purchase is refunded, Pro access from that purchase ends immediately.",
    refundContact:
      "If you have questions about subscriptions, passes, or refunds, you can contact us at hello@eltumvedrid.is.",
    refundClose: "Close",
    footerRefund: "Refunds",

    nearbyCampsite: "a nearby campsite",
    top5BestThisWeek: "Best this week",

    footerPricing: "Pricing",

    top5DistanceFromUserNote: "Distance is based on your current location.",
    top5DistanceFromUserTooltip: "Distances in this list are calculated from your location.",
    top5DistanceFromUserNoteWithLocation: "Showing results near:",
    unknownLocation: "Unknown location",

    bestWeatherWindow: "The best weather window today is from {from} to {to}.",

    labelTemp: "Temp",
    labelWind: "Wind",
    labelGust: "Gust",
    labelRain: "Rain",
    labelPrecipProbability: "Precip. probability",

    badgeGood: "Good time",
    badgeRough: "Rough conditions",

    decisionMoveTitle: "Move",
    decisionMoveBody: "Better weather is likely at {site}.",

    blogBadge: "Chase the Weather Blog",
    blogTitle: "Weather tips and campsite planning for Iceland",
    blogIntro:
      "Practical articles focused on camper travel, wind, rain, campsite decisions, and better weather nearby.",
    blogPublishedLabel: "Published",
    blogReadArticle: "Read article",
    blogMidCtaTitle: "Not sure where to go next?",

    backToBlog: "Back to blog",
    blogPostNotFoundTitle: "Article not found",
    blogPostNotFoundText: "The article you tried to open does not exist or may have been removed.",

    blogCtaTitle: "Check Conditions Now",
    blogCtaText: "See where the weather looks better nearby.",
    blogCtaButton: "Open Chase the Weather",
    blogDraftPreviewBanner: "Draft preview — this post is not published.",
    blogPreviewButton: "Preview",
    blogBackToBlog: "Back to blog",
    blogPublished: "Published",
    blogUpdated: "Updated",
    blogNearbyAttractions: "Places Worth Visiting",
    blogNearbyPlaces: "Nearby",
    blogNearbyWeatherCtaTitle: "Check Nearby Weather",
    blogNearbyWeatherCtaButton: "Compare Nearby Weather",
    blogWeatherContext: "Weather context",
    blogStayOrMove: "Stay or move?",
    blogWhyThisArea: "Why this area?",

    top5BestOverallThisWeek: "Best overall this week",
    decisionMoveBodyWindowAware:
      "Better weather is likely at {site} over the next few days, even if your current campsite scores best overall this week.",

    mapYouAreHere: "You are here",
    mapLoadingForecast: "Loading forecast…",
    mapErrorPrefix: "Error:",
    mapWeeklyScore: "Weekly score",
    mapCondition: "Condition",
    mapConditionGood: "Good",
    mapConditionFair: "Fair",
    mapConditionRough: "Rough",
    mapBasedOnNext7Days: "Based on next 7 days combined",
    mapOverlayOn: "Weather colors are on",
    mapOverlayHint: "Turn on weather colors to color-code campsites",
    mapLoadFailedTitle: "Unable to load map.",
    mapLoadFailedText: "Please refresh the page or check your connection.",
    mapHideWeatherColors: "Hide weather colors",
    mapShowWeatherColors: "Show weather colors",
    mapWeatherConditions: "Weather conditions",
    mapWinterMode: "Winter mode",

    // Weather Finder
    weatherFinderTitle: "Explore campsites by conditions",
    weatherFinderCalmest: "Calmest",
    weatherFinderWarmest: "Warmest",
    weatherFinderDriest: "Driest",
    weatherFinderNoResults: "No matching campsites found for this filter.",
    weatherFinderNoResultsHint: "Try increasing the radius or changing the time range.",
    weatherFinderUnlimited: "Unlimited",
    weatherFinderUpgradeForMore: "Upgrade to Pro to see all results",
    weatherFinderAvgWind: "avg wind",
    // Ticket #444 — replaces the retired weatherFinderAvgTemp key (which
    // literally claimed an "avg temp" that was actually a mean of daily
    // highs with missing days coerced to 0°C). weatherFinderWarmestLabel
    // only ever renders alongside a verified-complete window; an incomplete
    // window shows weatherFinderInsufficientData instead, never a number.
    weatherFinderWarmestLabel: "Average daily high",
    weatherFinderInsufficientData: "Insufficient data",
    weatherFinderPeriodLine: "First {n} forecast days, including today",
    weatherFinderRainDays: "rain days",
    weatherFinderRainDay: "rain day",
    weatherFinderRadius: "Radius",
    weatherFinderDays: "Days",
    weatherFinderResultTitleCalmest: "Calmest nearby campsites",
    weatherFinderResultTitleWarmest: "Warmest nearby campsites",
    weatherFinderResultTitleDriest: "Driest nearby campsites",
    weatherFinderShowFull: "Show full ranking",
    weatherFinderShowTop10: "Show top 10 only",

    // UX Miði #376 follow-up — progressive disclosure entrypoint. Collapsed
    // header reuses the existing weatherFinderTitle above (was previously
    // unused). RESULT_TITLE_KEY[mode] inside the expanded tool is unchanged.
    weatherFinderShowDetailsCta: "See sites ranked by weather",
    weatherFinderHideDetailsCta: "Hide ranking",

    // InstantComparison
    icReasonCalmer: "Calmer",
    icReasonDrier: "Drier",
    icReasonWarmer: "Warmer",
    icDistanceNearby: "Nearby option",
    icDistanceOther: "Another option",
    icDistanceFarther: "Further away",
    icWindDelta: "{pct}% less wind",
    icRainDelta: "{diff} mm less rain",
    icTempDelta: "{diff}°C warmer",
    icDistanceLabel: "~{km} km as the crow flies",
    // Ticket #444 — clarifies the icon-only temperature row's own numeric
    // value (Brochure-only; the homepage canonical card never shows it).
    icTempQualifier: "Average daily high, up to 3 days",
    // Weather Voice card eyebrow label (standalone card, not the verdict card).
    weatherVoiceLabel: "TJALDUR SAYS",
    // Ticket 408 (#408) — Weather Voice optional CTA labels. All five map
    // to the same kind of underlying exploration action; production
    // content never sets a non-null ctaType today (#406's 27 MVP entries
    // are all CTA=null), so these exist for the tested capability, not a
    // live production path. Wording describes exploring/comparing, never
    // promises a specific better site exists.
    weatherVoiceCtaBetterLocation: "Compare conditions",
    weatherVoiceCtaCalmerLocation: "See calmer spots",
    weatherVoiceCtaDrierLocation: "See drier spots",
    weatherVoiceCtaWarmerLocation: "See warmer spots",
    weatherVoiceCtaBestLocations: "See the best spots",
    // Ticket 410 (#410) — secondary share action, a separate seam from the
    // weather CTA above. Shown only for the conservative good/excellent
    // editorial promotion policy (weatherVoiceSharePolicy.js).
    weatherVoiceShareButtonLabel: "Share Tjaldur",
    weatherVoiceShareDialogTitle: "Share Tjaldur",
    weatherVoiceShareGenerating: "Preparing image…",
    weatherVoiceShareError: "Couldn't prepare the image.",
    weatherVoiceShareNative: "Share",
    weatherVoiceShareSave: "Save image",
    weatherVoiceShareCancelled: "Sharing cancelled.",
    weatherVoiceShareUnavailable: "Direct sharing didn't work — you can still save the image.",
    weatherVoiceShareImageAlt: "Share image preview",
    // Ticket 410 Revision 2 (#410, Ripley Round 1 finding #1) — explicit
    // daily-forecast/daily-high labeling in the exported image's context
    // line, never an unqualified temperature reading.
    weatherVoiceShareDailyForecastLabel: "Daily forecast",
    weatherVoiceShareHighTempLabel: "High",
    // Ticket 417 (#417) — compact two-action share choice (image vs
    // Facebook) shown before the existing image dialog content.
    weatherVoiceShareChoiceTitle: "Share Tjaldur",
    weatherVoiceShareChoiceImage: "Share image",
    weatherVoiceShareChoiceFacebook: "Share on Facebook",
    weatherVoiceShareBack: "Back",
    weatherVoiceShareFacebookOpensNewWindow: "Opens Facebook in a new window.",
    weatherVoiceShareFacebookUnavailable: "Facebook sharing isn't available for this comment yet — you can still share the image.",
    // Ticket 417 (#417) — scoped fallback for an unknown /share/tjaldur/v1/* path.
    shareFallbackTitle: "This shared comment isn't available",
    shareFallbackBody: "This link may be outdated, or the comment is no longer part of the current catalogue.",
    shareFallbackHomeLink: "Go to the homepage",
    // #396 Revision 3 (owner copy follow-up): removed the "isn't dangerous"
    // contrast — calm compare/monitor wording, consistent with canonical
    // consider semantics; does not imply moving is recommended.
    icConsiderFallback: "Conditions nearby may be a little different — worth comparing or keeping an eye on things.",
    icStayVerdict: "You are probably in a good spot.",
    icStayNoAlternative: "No nearby campsite clearly looks better over the next few days.",
    icLockedHeadline: "A better option may be nearby",
    icLockedBody: "Upgrade to Pro to see exactly where and why.",
    icImprovementStrong: "The weather looks much better over the next few days.",
    icImprovementWeak: "This campsite might be slightly better, but the difference is not large.",
    icImprovementMixed: "The weather at this location seems similar over the next 3 days.",
    icCtaView: "View this option",
    icCtaCompare: "View comparison",

    // RoutePlannerCard
    settingsChangeDistanceWindow: "Change distance and time window",

    // Toolbar
    settingsLabel: "Settings",
    logoutLabel: "Log out",
    loggingOutLabel: "Logging out…",
    logoutFailed: "Could not log out. Please try again.",

    // CampsitePicker
    campsitePickerNoMatches: "No matches.",
    campsitePickerAriaLabel: "Campsite picker",

    // Campsite comparison
    comparisonTitle: "Comparison",
    comparisonIntro: "Compare two campsites and see which one is calmer, drier or warmer over the next few days.",
    comparisonSiteALabel: "First campsite",
    comparisonSiteBLabel: "Second campsite",
    comparisonSameSite: "Choose two different campsites to compare.",
    comparisonEmpty: "Choose two campsites to see a comparison.",
    comparisonLoading: "Loading comparison…",
    comparisonError: "Could not load the comparison. Please try again.",
    comparisonNoHourly: "Hourly forecast is not available for this day.",
    comparisonDaySimilar: "Similar conditions on this day.",
    comparisonDayBetter: "{site} looks better on this day.",
    comparisonReasonCalmer: "calmer",
    comparisonReasonDrier: "drier",
    comparisonReasonWarmer: "warmer",
    comparisonShowHourly: "Show hourly",
    comparisonHideHourly: "Hide hourly",
    comparisonHrCalmer: "{diff} m/s calmer",
    comparisonHrCalmerGust: "{diff} m/s calmer gusts",
    comparisonHrDrier: "{diff} mm drier",
    comparisonHrWarmer: "{diff}°C warmer",
    comparisonHrSimilar: "Similar",
    comparisonFactorWind: "Wind",
    comparisonFactorGusts: "Gusts",
    comparisonFactorRain: "Rain",
    comparisonFactorTemp: "High temperature", // Ticket #444 — was bare "Temp"; this row is always the day's own temperature_2m_max, never an average
    comparisonLockedHeadline: "You've used your free comparison",
    comparisonLockedBody:
      "With Pro, you can compare campsites as often as you like and see day-by-day and hourly differences.",
    comparisonLockedCta: "Unlock unlimited comparison with Pro",

    // One-time travel passes
    pricingPass30Title: "30-Day Travel Pass",
    pricingPassYearTitle: "Annual Pass",
    pricingPerOneTime: "one-time",
    pricingPassMicro: "One-time purchase · No auto-renewal",
    pricingPass30Duration: "30 days from purchase date",
    pricingPassYearDuration: "1 year (365 days) from purchase date",
    pricingPass30RenewalText:
      "One-time purchase. Pro access for 30 days from the date of purchase. No automatic renewal.",
    pricingPassYearRenewalText:
      "One-time purchase. Pro access for 1 year (365 days) from the date of purchase. No automatic renewal.",
    pricingPass30CTA: "Buy 30-day pass",
    pricingPassYearCTA: "Buy annual pass",
    pricingPassCtaConfirm: "One-time purchase. No automatic renewal.",
    pricingPassPopularBadge: "Most popular for travellers",
    pricingFeatureNoRenewal: "No automatic renewal",

    locale: "en-GB",
  },
  is: {
    appTitle: "Tjaldsvæði á Íslandi",
    sevenDayWeather: "7 daga veður í nágrenni",
    heroStayMoveTitle: "Finndu rólegra tjaldsvæði áður en veðrið versnar.",
    heroStayMoveSubtitle: "Eltum Veðrið ber saman tjaldsvæði í nágrenninu og sýnir hvar líklegt er að vera rólegra, þurrara eða hlýrra næstu daga.",
    heroCta: "Finna betri stað",
    // Ticket 411 (#411) — homepage hero seasonal variants, see src/config/homepageHero.js
    heroWinterAuroraTitle: "Finndu besta veðrið — og bestu líkurnar á norðurljósum",
    heroWinterAuroraSubtitle: "Berðu saman veðrið um landið, finndu skárri áfangastað og sjáðu hvar aðstæður til norðurljósaskoðunar eru bestar.",
    heroAprilTitle: "Finndu besta veðrið",
    heroAprilSubtitle: "Berðu saman veðrið um landið og finndu skárri áfangastað.",
    heroWinterCta: "Skoða veðrið",
    myLocation: "Staðsetning mín",
    dark: "Dökkt",
    light: "Ljóst",
    top5Title: "Bestu staðirnir þessa vikuna",
    upToDate: "Uppfært.",
    campsite: "Tjaldsvæði",
    distance: "Fjarlægð",
    score: "Stig",
    day: "Dagur",
    weather: "Veður",
    sortedBy: "Raðað eftir vikustigum, síðan næst þér.",
    offlineRdMessage: "Eltum Veðrið er nú tilbúið til notkunar án nettengingar.",

    // Geolocation
    locating: "Leita að staðsetningu…",

    // Search & selection
    searchCampsites: "Leita að tjaldsvæðum…",
    selectCampsite: "Velja tjaldsvæði",
    selectOnMap: "Velja á korti",

    // Weather
    tempMin: "Minnsti hiti",
    tempMax: "Hæsti hiti",
    windMax: "Mesti vindur",
    rain: "Regn",

    tempMinShort: "Lágm.",
    tempMaxShort: "Hám.",
    windMaxShort: "Vindur",
    rainShort: "Regn",

    // Ratings (short)
    best: "Best",
    good: "Gott",
    ok: "Ok",
    fair: "Sæm",

    // Install
    installAsApp: "Setja upp",
    installOnDevice: "Setja upp",

    // Quality scale
    excellent: "Frábært",
    goodQuality: "Gott",
    fairQuality: "Sæmilegt",
    poor: "Slakt",
    bad: "Slæmt",

    // Weather descriptions
    clearSky: "Heiðskírt",
    mainlyClear: "Að mestu heiðskírt",
    partlyCloudy: "Hálfskýjað",
    overcast: "Alskýjað",
    unknownWeather: "Óþekkt veður",

    fog: "Þoka",
    rimeFog: "Hrímþoka",

    lightDrizzle: "Lítil súld",
    drizzle: "Súld",
    heavyDrizzle: "Mikil súld",

    lightRain: "Lítil rigning",
    heavyRain: "Mikil rigning",

    freezingRain: "Frostrigning",
    heavyFreezingRain: "Mikil frostrigning",

    lightSnow: "Lítil snjókoma",
    snow: "Snjókoma",
    heavySnow: "Mikil snjókoma",
    snowGrains: "Snjóél",

    showers: "Skúrir",
    heavyShowers: "Miklar skúrir",
    violentShowers: "Miklar skúrir með hvössu veðri",

    thunderstorm: "Þrumuveður",
    thunderHail: "Þruma og haglél",
    severeThunderHail: "Mikið þrumuveður og haglél",

    // Ticket 402 (#402) — sjá EN kafla fyrir samhengi.
    dailySummaryRainEarlyDryLater: "Rigning framan af, þurrt síðar",
    dailySummaryDryEarlyRainLater: "Þurrt framan af, rigning síðar",
    dailySummaryBriefShowers: "Stuttar skúrir, annars þurrt",

    // Stigaskýring
    scoreExplanationTitle: "Hvernig aðstæðurnar eru metnar",

    scoreExplanationIntro:
      "Eltum Veðrið gefur hverjum degi einkunn frá 0 til 10 byggt á veðuraðstæðum á tjaldsvæðinu.",

    scoreExplanationFinalTitle: "Lokaeinkunn",

    scoreExplanationFinalText:
      "Einkunnin byggir fyrst og fremst á hitastigi og er síðan aðlöguð eftir vindi og úrkomu. Lokaeinkunnin er alltaf á bilinu 0 til 10.",

    scoreExplanationOutro:
      "Hærri einkunn þýðir yfirleitt rólegri og þægilegri aðstæður til útilegu.",

    scoreExplanation_Hiti: "Hiti",
    scoreExplanation_Vindur: "Vindur",
    scoreExplanation_Rigning: "Úrkoma",

    // Misc UI
    loadingMoreCampsites: "Hleð fleiri stöðum…",
    weeklyScore: "Vikueinkunn:",
    from: "af",
    scored: "reiknuð",
    away: "frá",
    total: "Samtals",
    days: "dagar",
    pts: "stig",

    // UX Miði #376 — progressive disclosure entrypoints (forecast/ranking
    // supporting-detail, collapsed by default fyrir neðan HomeDecisionCard).
    forecastShowDetailsCta: "Sjá nánari veðurspá",
    forecastHideDetailsCta: "Fela veðurspá",

    // Pro (Vindátt og skjól)
    proPreviewCta: "Forskoðun Pro",
    windDirectionShelterTitle: "Vindátt og skjól",
    windDirectionShelterSubtitle:
      "Opnaðu vindátt og skjólstuðul til að velja skjólmeiri tjaldsvæði.",
    windLabel: "VINDUR",
    shelterLabel: "SKJÓL",
    shelteredFrom: "skjól frá",
    shelterHigh: "Mikið skjól",
    shelterMedium: "Meðal skjól",
    shelterLow: "Lítið skjól",
    seePro: "Sjá Pro",
    shelterTooltipPrefix: "Skjól",

    // Toasts / retry
    retrying: "Reyni aftur…",
    retry: "Reyna aftur",

    aboutTitle: "Um Eltum Veðrið",

    aboutIntro:
      "Eltum Veðrið hjálpar þér að sjá hvar veðrið lítur betur út næstu daga.",

    aboutGoal:
      "Eltum Veðrið ber saman aðstæður í nágrenninu og bendir á þegar betra veður gæti verið stutt frá.",

    aboutFeaturesTitle: "Eltum Veðrið býður upp á:",

    aboutFeatures: [
      "7 daga yfirsýn yfir aðstæður",
      "Greiningu á vindi, rigningu og hitastigi",
      "Yfirsýn yfir bestu staðina þessa vikuna",
      "Leiðsögn sem hjálpar þér að ákveða hvort betra sé að vera áfram eða færa sig",
    ],

    aboutProTitle: "Í Pro útgáfu bætist við:",

    aboutProFeatures: [
      "Vindátt",
      "Skjólstuðull sem hjálpar að finna rólegri staði",
      "Nánari samanburður á stöðum",
      "Meiri innsýn í hvenær gæti borgað sig að færa sig",
    ],

    aboutAuroraTitle: "Norðurljós",
    // Ticket 416 (#416) Round 2 leiðrétting: fyrra orðalagið ("á þínu
    // tjaldsvæði") gaf ranglega til kynna staðbundið mat fyrir valið
    // tjaldsvæði — NorthernLightsCard notar alltaf fasta staðalistann
    // AURORA_CANDIDATE_LOCATION_IDS (src/config/auroraCandidates.js),
    // óháð völdu tjaldsvæði.
    aboutAuroraBody:
      "Við metum norðurljósavirkni ásamt staðbundnum veðurskilyrðum til að meta aðstæður til norðurljósaskoðunar í kvöld á þeim stöðum sem við skoðum.",
    aboutAuroraProNote: "Nafngreindir staðir, ástæður og samanburður staða, þegar niðurstöður leyfa.",
    aboutAuroraLink: "Skoða norðurljósaspána",

    aboutOutro:
      "Eltum Veðrið er byggt fyrir íslenskt veður og aðstæður á ferðalögum.",

    aboutContact: "Ertu með spurningu, hugmynd eða lendir í vandræðum?",

    aboutEmailLabel: "Sendu okkur póst á",

    aboutSupportTitle: "Stuðningur",
    aboutSupportBody:
      "Eltum Veðrið hefur hlotið styrk frá Tækniþróunarsjóði til áframhaldandi þróunar verkefnisins.",
    aboutSupportLogoAlt: "Merki Tækniþróunarsjóðs",

    footerDataBy: "Gögn frá Open-Meteo. Spáin inniheldur hitastig, úrkomu, vind og veðurkóða.",
    footerAbout: "Um",
    blogNav: "Blog",
    footerContact: "Hafa samband",
    footerSupport: "Með stuðningi frá Tækniþróunarsjóði",

    backToForecast: "Til baka",

    brandTagline: "Finndu betra veður",

    nearest: "Næst",

    // Login / Identity (FLAT keys)
    close: "Loka",
    loginEmailPlaceholder: "nafn@domain.is",
    noPasswordNeeded:
      "Lykilorð er ekki nauðsynlegt til að skrá sig inn. Sláðu bara inn netfangið þitt og við sendum þér tengil til að skrá þig inn.",
    login: "Innskráning",
    enterEmailToContinue: "Sláðu inn netfang til að halda áfram",
    loading: "Hleð inn...",
    continue: "Haltu áfram",
    loginRequired: "Innskráning nauðsynleg",
    pleaseLoginToContinue: "Vinsamlegast skráðu þig inn til að halda áfram.",
    checkoutSuccess: "Pro opnað!",
    checkoutCancelled: "Greiðsluferli hætt.",

    // Pro CTA
    proCtaTitle: "Fá Pro aðgang",
    proCtaSubtitle: "Skráðu þig inn til að kaupa aðgang og opna Pro-fídusa.",
    proCtaTooltip: "Skráðu þig inn → farðu í greiðslu → opnaðu Pro",
    alreadyPro: "Þú ert þegar með Pro",
    proActive: "Pro virkt ✓",
    loggedIn: "Innskráður",
    proUpgrade: "Uppfæra í Pro – opna Ferðaráðgjafa",

    // Vikuröðun (Top5Leaderboard) — sértækt CTA, ekki sameiginlega
    // proUpgrade/proCtaSubtitle-textinn (sem vísar í Ferðaráðgjafa, ótengt þessu korti).
    weeklyRankingUpgradeCta: "Sjá fleiri bestu staði með Pro",
    weeklyRankingUpgradeBody:
      "Sjá fleiri af bestu tjaldsvæðunum og fá vindátt og skjólstuðul til að velja rólegri stað.",

    // UX Miði #376 — progressive disclosure entrypoint fyrir sjálft topplista-kortið
    // (aðgreint frá weeklyRankingUpgradeCta, sem er Pro-upsell inni í því).
    weeklyRankingShowDetailsCta: "Sjá fleiri staði",
    weeklyRankingHideDetailsCta: "Fela lista",

    // Billing portal
    proUntil: "Pro gilt til",
    proManageHint: "Stjórnaðu áskrift og kvittunum.",
    manageSubscription: "Stjórna",
    billingPortal: "Greiðslusíða",
    openingBillingPortal: "Opna greiðslugátt…",
    successStatusPending: "Bíður virkjunar",
    successDelayedActivation: "Aðgangur virkjast innan skamms. Ef þetta tekur lengri tíma en búist er við, endurhladdu síðuna.",
    offlineReadyTitle: "Tilbúið án nettengingar",
    offlineReadyMsg: "Eltum Veðrið er vistað og getur virkað án nettengingar.",

    availabilityClosedUntilMay: "Flest tjaldsvæði eru lokuð yfir veturinn (oft fram í maí).",
    availabilityOpenSeason: "Opið á sumartíma.",
    availabilityOpenAllYear: "Opið allt árið.",
    availabilityMostOpenInMay:
      "Flest tjaldsvæði opna í maí. Athugaðu hjá tjaldsvæðinu til staðfestingar.",
    availabilityClosedBadge: "Lokað (vetur)",
    seasonClosedInfo:
      "Mörg tjaldsvæði eru lokuð eins og er, en veðurgögnin hjálpa þér að skipuleggja ferðina eða ferðast utan háannatíma.",
    winterModeActive: "Vetrarhamur",
    winterModeTooltip: "Vindur og úrkoma vega þyngra en hitastig yfir vetrartímann.",
    windGust: "Vindhviða",

    top5DistanceFromUserNote: "Fjarlægð miðast við núverandi staðsetningu.",
    top5DistanceFromUserTooltip: "Fjarlægðir í þessum lista eru reiknaðar frá staðsetningu þinni.",
    top5DistanceFromUserNoteWithLocation: "Fjarlægðir miðast við núverandi staðsetningu:",
    unknownLocation: "Óþekkt staðsetning",

    termsTitle: "Skilmálar og fyrirvari",

    termsOperatorStatement: "CampCast er vörumerki sem notað er fyrir Eltum Veðrið.",

    termsIntro1:
      "Eltum Veðrið sýnir veðurupplýsingar og mat á aðstæðum byggt á tiltækum veðurgögnum. Upplýsingarnar eru eingöngu til viðmiðunar og geta verið ónákvæmar eða breyst hratt.",

    termsIntro2:
      "Veður og aðstæður á Íslandi geta breyst hratt og verið frábrugðin því sem birtist í þjónustunni.",

    termsIntro3:
      "Eltum Veðrið veitir ekki akstursleiðsögn og ábyrgist ekki öryggi ferðalaga. Notendur bera sjálfir ábyrgð á ferðalögum og ákvörðunum sínum og skulu ávallt kynna sér opinberar upplýsingar um veður og færð áður en haldið er af stað.",

    termsSourcesTitle: "Mælt er með að notendur skoði sérstaklega upplýsingar frá:",

    termsLiability:
      "Eltum Veðrið ber ekki ábyrgð á tjóni, töfum eða öðrum vandamálum sem kunna að verða vegna notkunar þjónustunnar eða upplýsinga sem þar birtast.",
    termsDisclaimerAgree: "Ég samþykki skilmála og fyrirvara",
    termsDisclaimerLink: "Skilmálar og fyrirvari",

    subscribeTermsPrefix: "Með því að halda áfram samþykkir þú",
    subscribeEmailHelp: "Netfangið verður notað fyrir kvittanir og áskriftarupplýsingar.",

    locationEnableDistances: "Kveiktu á staðsetningu til að sjá fjarlægðir",

    lockedPro: "Í boði í Pro",

    weatherNearbyHint: "Veður byggt á gögnum úr nágrenni",
    weatherNearbyTooltip:
      "Spáin byggir á næstu tiltæku veðurgögnum og getur verið örlítið frábrugðin aðstæðum á staðnum sjálfum.",
    precipLightRain: "Lítil rigning",
    precipModerateRain: "Miðlungs rigning",
    precipHeavyRain: "Mikil rigning",

    precipLightSnow: "Lítil snjókoma",
    precipModerateSnow: "Miðlungs snjókoma",
    precipHeavySnow: "Mikil snjókoma",
    precipVeryHeavyRain: "Mjög mikil rigning",
    precipVeryHeavySnow: "Mjög mikil snjókoma",

    precipLateRain: "Rigning seint",
    precipLateSnow: "Snjókoma seint",
    precipLight: "Smávægileg úrkoma",
    precipIntermittentRain: "Rigning með köflum",
    precipMixed: "Slydda og rigning",
    precipShowers: "Él",

    privacyTitle: "Persónuverndarstefna",
    privacyIntro1:
      "Eltum Veðrið leggur áherslu á að meðhöndla persónuupplýsingar á öruggan og ábyrgan hátt.",
    privacyIntro2:
      "Við söfnum nauðsynlegum upplýsingum, svo sem netfangi, reikningsupplýsingum og almennum notkunargögnum, til að reka þjónustuna og halda utan um áskriftir.",
    privacyPayments:
      "Greiðslur eru unnar af Paddle og greiðsluupplýsingar eru ekki geymdar hjá Eltum Veðrið.",
    privacyUsage:
      "Við getum einnig notað nafnlaus notkunargögn til að bæta virkni og notendaupplifun þjónustunnar.",
    privacySharing:
      "Eltum Veðrið selur ekki persónuupplýsingar og deilir þeim aðeins þegar það er nauðsynlegt til að vinna greiðslur, reka þjónustuna eða uppfylla lagaskyldur.",
    privacyRetention:
      "Persónuupplýsingar eru aðeins varðveittar eins lengi og nauðsynlegt er vegna þjónustunnar eða samkvæmt lögum.",
    privacyRights:
      "Notendur geta óskað eftir upplýsingum um gögn tengd reikningi sínum eða beðið um eyðingu þeirra með því að hafa samband við okkur.",
    privacyContactTitle: "Samskipti",
    privacyContactText:
      "Ef þú hefur spurningar um persónuvernd eða vilt óska eftir eyðingu gagna geturðu haft samband við okkur á:",
    privacyChanges:
      "Eltum Veðrið getur uppfært þessa persónuverndarstefnu af og til. Nýjasta útgáfa verður ávallt birt á vefsvæðinu.",
    privacyClose: "Loka",

    termsPrivacyAgreePrefix: "Ég samþykki",
    termsPrivacyAgreeAnd: "og",
    privacyLinkLabel: "persónuverndarstefnu",
    footerTerms: "Skilmálar",
    footerPrivacy: "Persónuvernd",

    refundTitle: "Endurgreiðslustefna",
    refundIntro:
      "Eltum Veðrið býður áskriftir (mánaðar- og ársáskrift) og einskiptispassa (30 daga ferðapassa og árspassa) fyrir aðgang að Pro eiginleikum.",
    refundCancel:
      "Notendur geta sagt upp áskrift hvenær sem er í gegnum greiðslugáttina. Aðgangur helst virkur út greitt tímabil.",
    refundPasses:
      "Passar eru einskiptiskaup án sjálfvirkrar endurnýjunar og renna sjálfkrafa út í lok gildistímans. Engin uppsögn er nauðsynleg.",
    refundWindow:
      "Eltum Veðrið veitir 14 daga endurgreiðslufrest frá fyrstu greiðslu nýrrar áskriftar og frá kaupum á passa.",
    refundAfterWindow:
      "Eftir að 14 daga endurgreiðslufresti lýkur eru greiðslur ekki endurgreiddar.",
    refundRenewals:
      "Endurgreiðslur eiga ekki við um endurnýjanir áskrifta nema lög kveði á um annað.",
    refundAccessRevoked:
      "Við endurgreiðslu lokast Pro-aðgangur viðkomandi kaupa samstundis.",
    refundContact:
      "Ef þú hefur spurningar um áskriftir, passa eða endurgreiðslur geturðu haft samband við hello@eltumvedrid.is.",
    refundClose: "Loka",
    footerRefund: "Endurgreiðslur",

    nearbyCampsite: "nálægt tjaldsvæði",
    top5BestThisWeek: "Best í vikunni",

    footerPricing: "Verðskrá",

    bestWeatherWindow: "Besti veðurglugginn í dag er frá {from} til {to}.",

    labelTemp: "Hiti",
    labelWind: "Vindur",
    labelGust: "Hviður",
    labelRain: "Regn",
    labelPrecipProbability: "Úrkomulíkur",

    badgeGood: "Góður tími",
    badgeRough: "Rysjótt",

    decisionMoveTitle: "Færðu þig",
    decisionMoveBody: "Betra veður er líklegt hjá {site}.",

    blogBadge: "Eltum Veðrið blogg",
    blogTitle: "Veðurráð og skipulag fyrir tjaldsvæði á Íslandi",
    blogIntro:
      "Hagnýtar greinar um ferðalög, veður og hvar betra gæti verið að vera næstu daga.",
    blogPublishedLabel: "Birt",
    blogReadArticle: "Lesa grein",
    blogMidCtaTitle: "Ekki viss hvert á að fara?",
    backToBlog: "Til baka í blogg",
    blogPostNotFoundTitle: "Grein fannst ekki",
    blogPostNotFoundText: "Greinin sem þú reyndir að opna er ekki til eða hefur verið fjarlægð.",

    blogCtaTitle: "Skoða aðstæður núna",
    blogCtaText: "Berðu saman staði í nágrenninu og sjáðu hvar veðrið lítur betur út.",
    blogCtaButton: "Opna Eltum Veðrið",
    blogDraftPreviewBanner: "Drög til forskoðunar — þessi grein er ekki birt.",
    blogPreviewButton: "Forskoðun",
    blogBackToBlog: "Til baka í blogg",
    blogPublished: "Birt",
    blogUpdated: "Uppfært",
    blogNearbyAttractions: "Vert að skoða",
    blogNearbyPlaces: "Í nágrenninu",
    blogNearbyWeatherCtaTitle: "Skoða veðrið í nágrenninu",
    blogNearbyWeatherCtaButton: "Berðu saman veðrið í nágrenninu",
    blogWeatherContext: "Veðurleg samhengi",
    blogStayOrMove: "Fara eða vera?",
    blogWhyThisArea: "Af hverju þetta svæði?",

    top5BestOverallThisWeek: "Best overall í vikunni",
    decisionMoveBodyWindowAware:
      "Betra veður er líklegt hjá {site} næstu daga, jafnvel þó núverandi tjaldsvæði skori hæst yfir vikuna í heild.",

    mapYouAreHere: "Þú ert hér",
    mapLoadingForecast: "Hleð veðurspá…",
    mapErrorPrefix: "Villa:",
    mapWeeklyScore: "Vikustig",
    mapCondition: "Ástand",
    mapConditionGood: "Gott",
    mapConditionFair: "Sæmilegt",
    mapConditionRough: "Slæmt",
    mapBasedOnNext7Days: "Byggt á næstu 7 dögum samanlagt",
    mapOverlayOn: "Veðurlitir eru virkir",
    mapOverlayHint: "Kveiktu á veðurlitum til að litakóða tjaldsvæði",
    mapLoadFailedTitle: "Ekki tókst að hlaða korti.",
    mapLoadFailedText: "Endurnýjaðu síðuna eða athugaðu nettenginguna.",
    mapHideWeatherColors: "Fela veðurliti",
    mapShowWeatherColors: "Sýna veðurliti",
    mapWeatherConditions: "Veðuraðstæður",
    mapWinterMode: "Vetrarhamur",

    // Weather Finder
    weatherFinderTitle: "Skoða staði eftir veðurskilyrðum",
    weatherFinderCalmest: "Rólegast",
    weatherFinderWarmest: "Hlýjast",
    weatherFinderDriest: "Þurrast",
    weatherFinderNoResults: "Engin tjaldsvæði fundust með þessum síum.",
    weatherFinderNoResultsHint: "Reyndu að auka radíus eða breyta tímabilinu.",
    weatherFinderUnlimited: "Ótakmarkað",
    weatherFinderUpgradeForMore: "Uppfærðu í Pro til að sjá fleiri niðurstöður",
    weatherFinderAvgWind: "meðalvindur",
    weatherFinderWarmestLabel: "Meðaltal hámarkshita",
    weatherFinderInsufficientData: "Ófullnægjandi gögn",
    weatherFinderPeriodLine: "Fyrstu {n} spádagar, dagurinn í dag meðtalinn",
    weatherFinderRainDays: "regnsdagar",
    weatherFinderRainDay: "regnsdagur",
    weatherFinderRadius: "Radíus",
    weatherFinderDays: "Dagar",
    weatherFinderResultTitleCalmest: "Rólegustu staðirnir í nágrenninu",
    weatherFinderResultTitleWarmest: "Hlýjustu tjaldsvæði í nágrenninu",
    weatherFinderResultTitleDriest: "Þurrstu tjaldsvæði í nágrenninu",
    weatherFinderShowFull: "Sýna alla röðun",
    weatherFinderShowTop10: "Sýna aðeins topp 10",

    // UX Miði #376 follow-up — progressive disclosure entrypoint. Collapsed
    // header endurnýtir weatherFinderTitle hér að ofan (var áður ónotað).
    // RESULT_TITLE_KEY[mode] inni í expanded tólinu er óbreytt.
    weatherFinderShowDetailsCta: "Sjá röðun staða eftir veðri",
    weatherFinderHideDetailsCta: "Fela röðun",

    // InstantComparison
    icReasonCalmer: "Rólegra",
    icReasonDrier: "Þurrara",
    icReasonWarmer: "Hlýrra",
    icDistanceNearby: "Nálægur kostur",
    icDistanceOther: "Annar kostur",
    icDistanceFarther: "Lengra í burtu",
    icWindDelta: "{pct}% minni vindur",
    icRainDelta: "{diff} mm minna regn",
    icTempDelta: "{diff}°C hlýrra",
    icDistanceLabel: "~{km} km í beinni línu",
    icTempQualifier: "Meðaltal hámarkshita, allt að 3 dagar",
    // Weather Voice card eyebrow label (standalone card, not the verdict card).
    weatherVoiceLabel: "TJALDUR SEGIR",
    // Ticket 408 (#408) — Weather Voice optional CTA labels. See the EN
    // block's comment for why production never renders these today.
    weatherVoiceCtaBetterLocation: "Bera saman aðstæður",
    weatherVoiceCtaCalmerLocation: "Skoða rólegri staði",
    weatherVoiceCtaDrierLocation: "Skoða þurrari staði",
    weatherVoiceCtaWarmerLocation: "Skoða hlýrri staði",
    weatherVoiceCtaBestLocations: "Skoða bestu staðina",
    // Ticket 410 (#410) — see EN block's comment.
    weatherVoiceShareButtonLabel: "Deila Tjaldi",
    weatherVoiceShareDialogTitle: "Deila Tjaldi",
    weatherVoiceShareGenerating: "Undirbý mynd…",
    weatherVoiceShareError: "Ekki tókst að útbúa mynd.",
    weatherVoiceShareNative: "Deila",
    weatherVoiceShareSave: "Vista mynd",
    weatherVoiceShareCancelled: "Deiling hætt við.",
    weatherVoiceShareUnavailable: "Ekki tókst að deila beint — hægt er að vista myndina í staðinn.",
    weatherVoiceShareImageAlt: "Forskoðun myndar til að deila",
    // Ticket 410 Revision 2 (#410) — sjá EN blokk.
    weatherVoiceShareDailyForecastLabel: "Dagsspá",
    weatherVoiceShareHighTempLabel: "Hámark",
    // Ticket 417 (#417) — sjá EN blokk.
    weatherVoiceShareChoiceTitle: "Deila Tjaldi",
    weatherVoiceShareChoiceImage: "Deila mynd",
    weatherVoiceShareChoiceFacebook: "Deila á Facebook",
    weatherVoiceShareBack: "Til baka",
    weatherVoiceShareFacebookOpensNewWindow: "Opnar Facebook í nýjum glugga.",
    weatherVoiceShareFacebookUnavailable: "Ekki er hægt að deila þessu ummæli á Facebook núna — samt er hægt að deila myndinni.",
    shareFallbackTitle: "Þetta deilda ummæli er ekki tiltækt",
    shareFallbackBody: "Þessi tengill gæti verið úreltur, eða ummælið er ekki lengur hluti af núverandi safni.",
    shareFallbackHomeLink: "Fara á forsíðuna",
    icConsiderFallback: "Aðstæður í nágrenninu gætu verið örlítið öðruvísi — vert að bera saman eða fylgjast með.",
    icStayVerdict: "Þú ert líklega á góðum stað.",
    icStayNoAlternative: "Enginn nálægur staður lítur greinilega betur út næstu daga.",
    icLockedHeadline: "Betri kostur gæti verið í nágrenninu",
    icLockedBody: "Uppfærðu í Pro til að sjá nákvæmlega hvar og hvers vegna.",
    icImprovementStrong: "Veðrið lítur mun betur út næstu daga.",
    icImprovementWeak: "Þetta tjaldsvæði gæti verið aðeins betra, en munurinn er ekki mikill.",
    icImprovementMixed: "Veðrið á þessum stað virðist svipað miðað við næstu 3 daga.",
    icCtaView: "Skoða þennan kost",
    icCtaCompare: "Skoða samanburð",

    // RoutePlannerCard
    settingsChangeDistanceWindow: "Breyta fjarlægð og tímaglugga",

    // Toolbar
    settingsLabel: "Stillingar",
    logoutLabel: "Skrá út",
    loggingOutLabel: "Skrái út…",
    logoutFailed: "Ekki tókst að skrá út. Reyndu aftur.",

    // CampsitePicker
    campsitePickerNoMatches: "Engar niðurstöður.",
    campsitePickerAriaLabel: "Tjaldsvæðisval",

    // Samanburður tjaldsvæða
    comparisonTitle: "Samanburður",
    comparisonIntro: "Berðu saman tvö tjaldsvæði og sjáðu hvort er rólegra, þurrara eða hlýrra næstu daga.",
    comparisonSiteALabel: "Fyrra tjaldsvæði",
    comparisonSiteBLabel: "Seinna tjaldsvæði",
    comparisonSameSite: "Veldu tvö ólík tjaldsvæði til að bera saman.",
    comparisonEmpty: "Veldu tvö tjaldsvæði til að sjá samanburð.",
    comparisonLoading: "Sæki samanburð…",
    comparisonError: "Ekki tókst að sækja samanburðinn. Reyndu aftur.",
    comparisonNoHourly: "Klukkutímaspá er ekki tiltæk fyrir þennan dag.",
    comparisonDaySimilar: "Svipaðar aðstæður þennan dag.",
    comparisonDayBetter: "{site} lítur betur út þennan dag.",
    comparisonReasonCalmer: "rólegra",
    comparisonReasonDrier: "þurrara",
    comparisonReasonWarmer: "hlýrra",
    comparisonShowHourly: "Sýna klukkutíma",
    comparisonHideHourly: "Fela klukkutíma",
    comparisonHrCalmer: "{diff} m/s rólegra",
    comparisonHrCalmerGust: "{diff} m/s minni hviður",
    comparisonHrDrier: "{diff} mm þurrara",
    comparisonHrWarmer: "{diff}°C hlýrra",
    comparisonHrSimilar: "Svipað",
    comparisonFactorWind: "Vindur",
    comparisonFactorGusts: "Hviður",
    comparisonFactorRain: "Úrkoma",
    comparisonFactorTemp: "Hámarkshiti", // Ticket #444 — var "Hiti"; þessi lína er alltaf temperature_2m_max dagsins, ekki meðaltal
    comparisonLockedHeadline: "Þú hefur notað ókeypis samanburðinn",
    comparisonLockedBody:
      "Með Pro geturðu borið saman tjaldsvæði eins oft og þú vilt og séð dag- og klukkutímamun.",
    comparisonLockedCta: "Opna ótakmarkaðan samanburð með Pro",

    // Eingreiðslu ferðapassar
    pricingPass30Title: "30 daga ferðapassi",
    pricingPassYearTitle: "Árspassi",
    pricingPerOneTime: "eingreiðsla",
    pricingPassMicro: "Eingreiðsla · Engin sjálfvirk endurnýjun",
    pricingPass30Duration: "30 dagar frá kaupdegi",
    pricingPassYearDuration: "1 ár (365 dagar) frá kaupdegi",
    pricingPass30RenewalText:
      "Eingreiðsla. Pro aðgangur í 30 daga frá kaupdegi. Engin sjálfvirk endurnýjun.",
    pricingPassYearRenewalText:
      "Eingreiðsla. Pro aðgangur í 1 ár (365 dagar) frá kaupdegi. Engin sjálfvirk endurnýjun.",
    pricingPass30CTA: "Kaupa 30 daga passa",
    pricingPassYearCTA: "Kaupa árspassa",
    pricingPassCtaConfirm: "Eingreiðsla. Engin sjálfvirk endurnýjun.",
    pricingPassPopularBadge: "Vinsælast fyrir ferðamenn",
    pricingFeatureNoRenewal: "Engin sjálfvirk endurnýjun",

    locale: "is-IS",
  },
};
